
/**
 * Staff Selector Component
 * Displays current staff and allows selection from staff list
 */

import React, { useState, useEffect, useCallback } from 'react';
import { IconButton, Menu, MenuItem, Tooltip, Typography, CircularProgress, Divider, Button, TextField, Box, Chip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { apiFetchJson } from '../api/dispatchClient';
import { mergeStaffPriorities, onStaffPriorityChange } from '../api/dispatchService';
import { isOnDuty } from '../utils/staffPriority';
import StaffDutyDialog from './StaffDutyDialog';

interface StaffData {
  id: number | null;
  name: string;
  login_id: string;
}

interface StaffSelectorProps {
  color?: any;
  size?: 'small' | 'medium' | 'large';
  user?: any;
  [key: string]: any;
}

/**
 * Staff Selector Icon with Menu
 */
export function StaffSelector({ color = 'inherit', size = 'medium', user, ...rest }: StaffSelectorProps): React.ReactElement {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [staffList, setStaffList] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [currentStaff, setCurrentStaff] = useState<StaffData | null>(null);
    const [tempName, setTempName] = useState<string>('');
    const [dutyDialogOpen, setDutyDialogOpen] = useState(false);
    const open = Boolean(anchorEl);

    // Load current staff from localStorage on mount
    useEffect(() => {
        const savedStaff = localStorage.getItem('bo_staff');
        if (savedStaff) {
            try {
                setCurrentStaff(JSON.parse(savedStaff));
            } catch (e) {
                console.error('Failed to parse saved staff:', e);
            }
        }
    }, []);

    // Sync user to staff if not 'staff' account
    useEffect(() => {
        if (user && user.username !== 'staff') {
            const autoStaff: StaffData = {
                id: user.id,
                name: user.name,
                login_id: user.username
            };

            // Update if different to maintain single source of truth
            if (!currentStaff || currentStaff.id !== autoStaff.id || currentStaff.name !== autoStaff.name) {
                setCurrentStaff(autoStaff);
                localStorage.setItem('bo_staff', JSON.stringify(autoStaff));
                (window as any).bo_staff = autoStaff;
            }
        }
    }, [user, currentStaff]);

    const fetchStaff = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiFetchJson('staff?active=1');
            if (response && response.data) {
                const activeStaff = response.data.filter(
                    (staff: any) => staff.active === 1 && !staff.is_deleted && staff.is_active !== 0
                );
                // localStorageのpriority情報をマージ
                const merged = mergeStaffPriorities(activeStaff);
                // priority降順ソート（出勤者が上、オフが下）
                merged.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));
                setStaffList(merged);
            }
        } catch (error) {
            console.error('Failed to fetch staff list:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setTempName(currentStaff?.name || '');
        // 毎回取得して最新のpriority反映
        await fetchStaff();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleTempNameSubmit = () => {
        if (!tempName.trim()) return;
        const staffData: StaffData = {
            id: null,
            name: tempName,
            login_id: 'temp'
        };
        setCurrentStaff(staffData);
        localStorage.setItem('bo_staff', JSON.stringify(staffData));
        (window as any).bo_staff = staffData;
        handleClose();
    };

    const handleStaffSelect = (staff: any) => {
        const staffData: StaffData = {
            id: staff.id,
            name: staff.staff_name,
            login_id: staff.staff_login_id
        };

        setCurrentStaff(staffData);
        localStorage.setItem('bo_staff', JSON.stringify(staffData));

        // Set as global variable
        (window as any).bo_staff = staffData;

        handleClose();
    };

    const handleClearStaff = () => {
        setCurrentStaff(null);
        localStorage.removeItem('bo_staff');
        (window as any).bo_staff = null;
        handleClose();
    };

    const handleOpenDutyDialog = () => {
        handleClose(); // メニューを閉じる
        setDutyDialogOpen(true);
    };

    const handleDutySaved = useCallback(() => {
        // 保存後にスタッフリストを即座に再マージ
        setStaffList(prev => {
            if (prev.length === 0) return prev;
            const merged = mergeStaffPriorities(prev);
            merged.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));
            return merged;
        });
    }, []);

    // 優先度変更リスナー（他タブ or 同一タブからの更新を即座に反映）
    useEffect(() => {
        const cleanup = onStaffPriorityChange(() => {
            setStaffList(prev => {
                if (prev.length === 0) return prev;
                const merged = mergeStaffPriorities(prev);
                merged.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));
                return merged;
            });
        });
        return cleanup;
    }, []);

    const displayText = currentStaff
        ? `対応中:${currentStaff.name}`
        : '対応スタッフ';

    // 出勤スタッフとオフスタッフを分離
    const onDutyStaff = staffList.filter(s => isOnDuty(s.priority));
    const offDutyStaff = staffList.filter(s => !isOnDuty(s.priority));

    return (
        <>
            <Button
                color={color}
                onClick={(!user || user.username === 'staff') ? handleClick : undefined}
                aria-label="対応スタッフを選択"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textTransform: 'none',
                    lineHeight: 1.2,
                    minWidth: 'auto',
                    py: 0.5,
                    px: 1
                }}
            >
                <PersonIcon />
                <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.2, whiteSpace: 'nowrap' }}>
                    {displayText}
                </Typography>
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    style: {
                        maxHeight: 500,
                        width: '280px',
                    },
                }}
            >
                {currentStaff && (
                    <>
                        <MenuItem disabled>
                            <Typography variant="caption" color="text.secondary">
                                現在の対応スタッフ
                            </Typography>
                        </MenuItem>
                        <MenuItem>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {currentStaff.name}
                            </Typography>
                        </MenuItem>
                        <MenuItem onClick={handleClearStaff}>
                            <Typography variant="body2" color="error">
                                クリア
                            </Typography>
                        </MenuItem>
                        <Divider />
                    </>
                )}

                {/* 本日勤務ボタン */}
                <Box sx={{ px: 2, py: 0.5 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<CalendarTodayIcon />}
                        onClick={handleOpenDutyDialog}
                        sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                    >
                        本日勤務
                    </Button>
                </Box>
                <Divider sx={{ my: 0.5 }} />

                <MenuItem disabled>
                    <Typography variant="caption" color="text.secondary">
                        スタッフ選択
                    </Typography>
                </MenuItem>

                {loading ? (
                    <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2">読み込み中...</Typography>
                    </MenuItem>
                ) : (
                    <>
                        {/* 出勤スタッフ */}
                        {onDutyStaff.length > 0 && (
                            <>
                                {onDutyStaff.map((staff) => (
                                    <MenuItem
                                        key={staff.id}
                                        onClick={() => handleStaffSelect(staff)}
                                        selected={currentStaff?.id === staff.id}
                                    >
                                        <Chip
                                            label="出勤"
                                            size="small"
                                            color="success"
                                            sx={{ mr: 1, height: 20, fontSize: '0.6rem' }}
                                        />
                                        <Typography variant="body2">
                                            {staff.staff_name}
                                        </Typography>
                                    </MenuItem>
                                ))}
                                {offDutyStaff.length > 0 && <Divider sx={{ my: 0.5 }} />}
                            </>
                        )}
                        {/* オフスタッフ */}
                        {offDutyStaff.map((staff) => (
                            <MenuItem
                                key={staff.id}
                                onClick={() => handleStaffSelect(staff)}
                                selected={currentStaff?.id === staff.id}
                            >
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    {staff.staff_name}
                                </Typography>
                            </MenuItem>
                        ))}
                        {staffList.length === 0 && !loading && (
                            <MenuItem disabled>
                                <Typography variant="body2" color="text.secondary">
                                    スタッフが見つかりません
                                </Typography>
                            </MenuItem>
                        )}
                    </>
                )}

                {user?.username === 'staff' && (
                    <>
                        <Divider />
                        <Box sx={{ p: 1, px: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                一時対応者
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="名前を入力"
                                value={tempName}
                                onChange={(e) => setTempName(e.target.value)}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') handleTempNameSubmit();
                                }}
                                onClick={(e) => e.stopPropagation()}
                                variant="outlined"
                            />
                        </Box>
                    </>
                )}
            </Menu>

            {/* 本日勤務モーダル */}
            <StaffDutyDialog
                open={dutyDialogOpen}
                onClose={() => setDutyDialogOpen(false)}
                staffList={staffList}
                onSaved={handleDutySaved}
            />
        </>
    );
}

export default StaffSelector;
